import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import { Motion, motion } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

export interface ToastPresenceProps
  extends MotionPropsFor<IntrinsicSystemProps<'div'>> {
  /**
   * Spacing to apply below the disclaimer toast. This must be
   * an explicit pixel value as it's animated away on exit.
   */
  spacing?: string;
  isVisible?: boolean;
}

/**
 * Mount and unmount a component using the same animation that is used for
 * toasts spawned via `useToast`. This should be used instead of `ToastTarget`
 * for cases where a toast-like component needs to be positioned relative to
 * another element (instead of the viewport), but we don't want to hijack the
 * positioning of all toasts displayed on a page.
 *
 * NOTE: this should only be used when it's only necesssary to display a single
 * element at a time. If multiple components need to be displayed in the
 * stacking/ephemeral style of `useToast`, use `ToastTarget` instead to control
 * the position of all toasts on the page.
 *
 * @todo(design-system): move this into the design system.
 *
 */
export const ToastPresence = ({
  spacing = '0px',
  children,
  isVisible = false,
  ...props
}: ToastPresenceProps) => (
  <Motion.Soft>
    <AnimatePresence>
      {isVisible && (
        <motion.div
          animate={{
            paddingBottom: spacing,
            opacity: 1,
            transform: 'scale(1)',
          }}
          exit={{
            height: 0,
            paddingBottom: '0px',
            transition: {
              paddingBottom: { delay: 0.25 },
              height: { delay: 0.25 },
            },
            opacity: 0,
            transform: 'scale(0.85)',
          }}
          initial={{
            opacity: 0,
            transform: 'scale(0.85)',
          }}
          overflow="visible"
          pb={spacing}
          {...props}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </Motion.Soft>
);
